/**
 * 🎮 MINI-JEUX - Dating gamifié à deux !
 * 
 * Jeux disponibles :
 * - "Tu préfères ?" - Choix absurdes ou profonds
 * - Quiz Compatibilité - Découvre à quel point vous matchez
 * - Blind Test - Devine la chanson préférée
 * - Vérité ou Défi - Le classique
 * 
 * Pourquoi ça marche :
 * - Fun = plus de temps sur l'app
 * - Brise la glace facilement
 * - Crée des sujets de conversation
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gamepad2, Trophy, Zap, Heart, Star, 
  ThumbsUp, ThumbsDown, RefreshCw, Send,
  Users, Clock, Sparkles, Gift
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// 🎲 Questions "Tu préfères ?"
const WOULD_YOU_RATHER = [
  {
    id: 1,
    optionA: "Pouvoir voler",
    optionB: "Pouvoir lire dans les pensées",
    category: "pouvoirs"
  },
  {
    id: 2,
    optionA: "Pizza froide au petit-déj",
    optionB: "Céréales au dîner",
    category: "food"
  },
  {
    id: 3,
    optionA: "Premier date au cinéma",
    optionB: "Premier date en randonnée",
    category: "dating"
  },
  {
    id: 4,
    optionA: "Vivre sans musique",
    optionB: "Vivre sans films/séries",
    category: "dilemme"
  },
  {
    id: 5,
    optionA: "Savoir comment tu vas mourir",
    optionB: "Savoir quand tu vas mourir",
    category: "deep"
  },
  {
    id: 6,
    optionA: "Toujours dire la vérité",
    optionB: "Toujours mentir",
    category: "deep"
  },
  {
    id: 7,
    optionA: "Voyage dans le passé",
    optionB: "Voyage dans le futur",
    category: "pouvoirs"
  },
  {
    id: 8,
    optionA: "Gagner au loto mais perdre tous tes amis",
    optionB: "Rester pauvre mais avoir les meilleurs amis",
    category: "deep"
  },
  {
    id: 9,
    optionA: "Être invisible",
    optionB: "Pouvoir te téléporter",
    category: "pouvoirs"
  },
  {
    id: 10,
    optionA: "Café le matin",
    optionB: "Thé le matin",
    category: "lifestyle"
  },
  {
    id: 11,
    optionA: "Plage et farniente",
    optionB: "Montagne et aventure",
    category: "travel"
  },
  {
    id: 12,
    optionA: "Chien",
    optionB: "Chat",
    category: "lifestyle"
  },
  {
    id: 13,
    optionA: "Petit-déj au lit",
    optionB: "Dîner aux chandelles",
    category: "dating"
  },
  {
    id: 14,
    optionA: "Connaître son âme sœur mais ne jamais la rencontrer",
    optionB: "Ne jamais savoir qui c'est mais peut-être la croiser",
    category: "deep"
  },
  {
    id: 15,
    optionA: "SMS",
    optionB: "Appels téléphoniques",
    category: "lifestyle"
  }
];

// 🧠 Questions Quiz Compatibilité
const COMPATIBILITY_QUIZ = [
  {
    id: 1,
    question: "Un dimanche idéal, c'est...",
    options: [
      { id: 'a', text: "Netflix et canapé", emoji: "🎬" },
      { id: 'b', text: "Brunch entre potes", emoji: "🥞" },
      { id: 'c', text: "Sport et nature", emoji: "🌳" },
      { id: 'd', text: "Culture et musées", emoji: "🏛️" }
    ]
  },
  {
    id: 2,
    question: "En couple, tu es plutôt...",
    options: [
      { id: 'a', text: "Fusionnel(le)", emoji: "👥" },
      { id: 'b', text: "Indépendant(e)", emoji: "🧘" },
      { id: 'c', text: "Équilibré(e)", emoji: "⚖️" },
      { id: 'd', text: "Ça dépend", emoji: "🤷" }
    ]
  },
  {
    id: 3,
    question: "Ta love language principale ?",
    options: [
      { id: 'a', text: "Mots doux", emoji: "💬" },
      { id: 'b', text: "Câlins et affection", emoji: "🤗" },
      { id: 'c', text: "Cadeaux", emoji: "🎁" },
      { id: 'd', text: "Moments de qualité", emoji: "✨" }
    ]
  },
  {
    id: 4,
    question: "Un conflit, tu le gères comment ?",
    options: [
      { id: 'a', text: "On en parle direct", emoji: "🗣️" },
      { id: 'b', text: "Je prends du recul d'abord", emoji: "🧘" },
      { id: 'c', text: "J'évite le conflit", emoji: "🙈" },
      { id: 'd', text: "Ça dépend de la situation", emoji: "🤔" }
    ]
  },
  {
    id: 5,
    question: "Ton red flag absolu ?",
    options: [
      { id: 'a', text: "Le mensonge", emoji: "🤥" },
      { id: 'b', text: "Le manque de respect", emoji: "😤" },
      { id: 'c', text: "La jalousie excessive", emoji: "😡" },
      { id: 'd', text: "Le manque d'ambition", emoji: "😴" }
    ]
  }
];

// 🎮 Composant "Tu Préfères ?"
export function WouldYouRather({ 
  partnerId, 
  partnerName,
  onAnswer, 
  onComplete,
  isDarkMode 
}) {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [myAnswers, setMyAnswers] = useState({});
  const [partnerAnswers, setPartnerAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  
  // Initialiser avec des questions random
  useEffect(() => {
    const shuffled = [...WOULD_YOU_RATHER].sort(() => Math.random() - 0.5).slice(0, 5);
    setQuestions(shuffled);
  }, []);
  
  const currentQuestion = questions[currentIndex];
  
  const handleAnswer = (choice) => {
    const newAnswers = { ...myAnswers, [currentQuestion.id]: choice };
    setMyAnswers(newAnswers);
    
    // Simuler réponse partenaire (en vrai ce serait via WebSocket)
    const partnerChoice = Math.random() > 0.5 ? 'A' : 'B';
    setPartnerAnswers(prev => ({ ...prev, [currentQuestion.id]: partnerChoice }));
    
    onAnswer && onAnswer(currentQuestion.id, choice);
    
    // Passer à la question suivante ou terminer
    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setShowResult(true);
        // Calculer score de compatibilité
        const matches = Object.keys(newAnswers).filter(
          qId => newAnswers[qId] === partnerAnswers[qId]
        ).length;
        
        if (matches >= 3) {
          confetti({ particleCount: 100, spread: 70 });
        }
      }
    }, 500);
  };
  
  // Calculer compatibilité
  const calculateCompatibility = () => {
    const matches = Object.keys(myAnswers).filter(
      qId => myAnswers[qId] === partnerAnswers[qId]
    ).length;
    return Math.round((matches / questions.length) * 100);
  };
  
  if (showResult) {
    const compat = calculateCompatibility();
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`rounded-2xl p-6 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}
      >
        <div className="text-5xl mb-4">
          {compat >= 80 ? '🔥' : compat >= 60 ? '💜' : compat >= 40 ? '😊' : '🤔'}
        </div>
        <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {compat}% de compatibilité !
        </h3>
        <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {compat >= 80 
            ? `Wow ! Toi et ${partnerName} pensez pareil ! 🔥`
            : compat >= 60
              ? `Belle connexion avec ${partnerName} ! 💜`
              : compat >= 40
                ? `Quelques points communs ! Creusez ça 😉`
                : `Les opposés s'attirent, non ? 😏`
          }
        </p>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              setCurrentIndex(0);
              setMyAnswers({});
              setPartnerAnswers({});
              setShowResult(false);
              const shuffled = [...WOULD_YOU_RATHER].sort(() => Math.random() - 0.5).slice(0, 5);
              setQuestions(shuffled);
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Rejouer
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white"
            onClick={onComplete}
          >
            <Send className="w-4 h-4 mr-2" />
            Discuter
          </Button>
        </div>
      </motion.div>
    );
  }
  
  if (!currentQuestion) return null;
  
  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🎲</span>
          <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Tu préfères ?
          </span>
        </div>
        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {currentIndex + 1}/{questions.length}
        </span>
      </div>
      
      {/* Progress */}
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full mb-6 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-pink-500 to-purple-500"
          initial={{ width: 0 }}
          animate={{ width: `${((currentIndex) / questions.length) * 100}%` }}
        />
      </div>
      
      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion.id}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          className="space-y-4"
        >
          <h4 className={`text-lg font-medium text-center mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Tu préfères...
          </h4>
          
          <div className="grid grid-cols-1 gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleAnswer('A')}
              className={`p-4 rounded-xl text-left transition-all ${
                isDarkMode
                  ? 'bg-blue-500/20 hover:bg-blue-500/40 text-blue-300 border border-blue-500/30'
                  : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200'
              }`}
            >
              <span className="font-medium">🔵 {currentQuestion.optionA}</span>
            </motion.button>
            
            <div className="text-center text-gray-400 text-sm">ou</div>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleAnswer('B')}
              className={`p-4 rounded-xl text-left transition-all ${
                isDarkMode
                  ? 'bg-pink-500/20 hover:bg-pink-500/40 text-pink-300 border border-pink-500/30'
                  : 'bg-pink-50 hover:bg-pink-100 text-pink-700 border border-pink-200'
              }`}
            >
              <span className="font-medium">🔴 {currentQuestion.optionB}</span>
            </motion.button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// 🧠 Quiz Compatibilité
export function CompatibilityQuiz({ 
  partnerId,
  partnerName,
  onComplete,
  isDarkMode 
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  
  const currentQuestion = COMPATIBILITY_QUIZ[currentIndex];
  
  const handleAnswer = (optionId) => {
    const newAnswers = [...answers, optionId];
    setAnswers(newAnswers);
    
    if (currentIndex < COMPATIBILITY_QUIZ.length - 1) {
      setTimeout(() => setCurrentIndex(currentIndex + 1), 300);
    } else {
      setShowResult(true);
      confetti({ particleCount: 80, spread: 60 });
    }
  };
  
  if (showResult) {
    // Score random pour la démo (en vrai ce serait calculé avec les réponses du partenaire)
    const score = Math.floor(Math.random() * 30) + 60;
    
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`rounded-2xl p-6 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}
      >
        <div className="relative w-32 h-32 mx-auto mb-4">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="12"
              fill="none"
              className="text-gray-200 dark:text-gray-700"
            />
            <motion.circle
              cx="64"
              cy="64"
              r="56"
              stroke="url(#gradient)"
              strokeWidth="12"
              fill="none"
              strokeLinecap="round"
              initial={{ strokeDasharray: '0 352' }}
              animate={{ strokeDasharray: `${score * 3.52} 352` }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#EC4899" />
                <stop offset="100%" stopColor="#8B5CF6" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {score}%
            </span>
          </div>
        </div>
        
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {score >= 80 ? '🔥 Match parfait !' : score >= 60 ? '💜 Belle compatibilité !' : '🤔 À explorer...'}
        </h3>
        <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Toi et {partnerName} avez {score}% de compatibilité !
        </p>
        
        <Button
          className="bg-gradient-to-r from-pink-500 to-purple-500 text-white"
          onClick={onComplete}
        >
          <Heart className="w-4 h-4 mr-2" />
          Continuer la discussion
        </Button>
      </motion.div>
    );
  }
  
  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🧠</span>
          <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Quiz Compatibilité
          </span>
        </div>
        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {currentIndex + 1}/{COMPATIBILITY_QUIZ.length}
        </span>
      </div>
      
      {/* Progress */}
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full mb-6 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-pink-500 to-purple-500"
          animate={{ width: `${((currentIndex) / COMPATIBILITY_QUIZ.length) * 100}%` }}
        />
      </div>
      
      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <h4 className={`text-lg font-medium mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {currentQuestion.question}
          </h4>
          
          <div className="grid grid-cols-2 gap-3">
            {currentQuestion.options.map((option) => (
              <motion.button
                key={option.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleAnswer(option.id)}
                className={`p-4 rounded-xl flex flex-col items-center gap-2 transition-all ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600'
                    : 'bg-gray-100 hover:bg-pink-50 border border-gray-200 hover:border-pink-300'
                }`}
              >
                <span className="text-2xl">{option.emoji}</span>
                <span className={`text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {option.text}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// 🎮 Lobby de jeux
export function GameLobby({ 
  partnerId, 
  partnerName, 
  onSelectGame,
  onClose,
  isDarkMode 
}) {
  const games = [
    {
      id: 'would-you-rather',
      name: 'Tu préfères ?',
      emoji: '🎲',
      description: 'Choix absurdes et dilemmes fous',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'compatibility-quiz',
      name: 'Quiz Compatibilité',
      emoji: '🧠',
      description: 'Découvre à quel point vous matchez',
      color: 'from-pink-500 to-purple-500'
    },
    {
      id: 'truth-or-dare',
      name: 'Vérité ou Défi',
      emoji: '🎭',
      description: 'Le classique indémodable',
      color: 'from-orange-500 to-red-500',
      comingSoon: true
    },
    {
      id: 'blind-test',
      name: 'Blind Test',
      emoji: '🎧',
      description: 'Devine ses goûts musicaux',
      color: 'from-green-500 to-emerald-500',
      comingSoon: true
    }
  ];
  
  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Gamepad2 className="w-6 h-6 text-pink-500" />
          <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Jouer avec {partnerName}
          </h3>
        </div>
      </div>
      
      <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Brisez la glace avec un mini-jeu ! 🎮
      </p>
      
      <div className="space-y-3">
        {games.map((game) => (
          <motion.button
            key={game.id}
            whileHover={{ scale: game.comingSoon ? 1 : 1.02 }}
            whileTap={{ scale: game.comingSoon ? 1 : 0.98 }}
            onClick={() => !game.comingSoon && onSelectGame(game.id)}
            disabled={game.comingSoon}
            className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all ${
              game.comingSoon
                ? 'opacity-50 cursor-not-allowed'
                : isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${game.color} flex items-center justify-center text-2xl`}>
              {game.emoji}
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {game.name}
                </span>
                {game.comingSoon && (
                  <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs">
                    Bientôt
                  </span>
                )}
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {game.description}
              </p>
            </div>
            {!game.comingSoon && (
              <Zap className="w-5 h-5 text-yellow-500" />
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export default GameLobby;
